import { IInitializer, IUserContext, IPushService, PushAppType, MessageModel } from "@truckdown/systems";
import { IAppUserContext } from "../IAppUserContext";
import { Constants } from '../constants';
import {
    useOneSignal,
    NotificationDismissEvent,
    NotificationForegroundWillDisplayEvent,
    NotificationClickEvent,
    IOneSignalOneSignal,
    IOSNotification
} from '@onesignal/onesignal-vue3';

const tdids: { [key: string]: string } = {
    'localhost': 'f075ef8a-a5c1-4319-9330-eef6d299958a',
    'prototype.truckdown.com': 'c4d2835c-f958-4261-8c4e-4de601037570',
    'test.truckdown.com': '55edf5b8-3dcc-4598-ab7c-f64f041162ff',
    'www.truckdown.com': 'f1a02b68-208f-4517-be40-a07dac27171b'
}

const planrvids: { [key: string]: string } = {
    'localhost': 'c10374e2-2eb1-40b0-9adb-558448ab1abf',
    'prototype.planrv.com': '590943a1-374f-45d4-8719-ff46369ce147',
    'test.planrv.com': '9176ec17-3ee2-438b-bb51-87148c472fbf',
    'www.planrv.com': 'cc0a9905-b2a0-4615-bf6d-ff2008f83d16'
}

const getId = function (context: IUserContext) {
    if (context.system == 'planrv') {
        let id = planrvids[window.location.hostname];
        if (id) {
            return id;
        }
    } else {
        let id = tdids[window.location.hostname];
        if (id) {
            return id;
        }
    }
}

const getPushAppType = function (context: IAppUserContext) {
    if (context.system == 'planrv') {
        return PushAppType.PlanRvWeb;
    }
    return PushAppType.TruckDownWeb;
}

const convertProperties = function (props?: Object) {
    let res = {} as { [key: string]: string };
    if (props) {
        for (let p in props) {
            if (props.hasOwnProperty(p) && typeof (props as any)[p] === 'string') {
                res[p] = (props as any)[p] as string;
            }
        }
    }
    return res;
};

const convertNotification = function (notification: IOSNotification) {
    return {
        title: notification.title,
        message: notification.body,
        link: notification.launchURL,
        properties: convertProperties(notification.additionalData)
    } as MessageModel;
}

class NotificationReceivedEventHandler implements NotificationReceivedEventHandler {
    constructor(id: string, message: MessageModel) {
        this.id = id;
        this.message = message;
    }

    id: string;
    message: MessageModel;

    isCancelled: boolean = false;

    preventDefault() {
        this.isCancelled = true;
    }
}

export class OneSignalInitializer implements IInitializer {
    name: string = 'OneSignalInitializer';
    stage: number = 0;
    order: number = 0;

    private async handlePermissionChange(context: IAppUserContext, allowRequest: boolean) {
        try {
            let initializer = this;
            if (context.oneSignal) {
                if (window.Notification.permission == 'default') {
                    if (allowRequest) {
                        await context.requestNotificationPermission();
                    }
                } else if (context.isAuthenticated && Notification.permission == 'granted') {
                    if (context.oneSignal.User.externalId) {
                        await initializer.touch(context);
                    } else {
                        await initializer.register(context);
                    }
                } else if (Notification.permission == 'denied') {
                    await initializer.deregister(context);
                }
            }
        }
        catch (err) {
            console.log(err);
        }
    }

    private async touch(context: IAppUserContext) {
        try {
            let initializer = this;
            if (context.oneSignal) {
                if (context.isAuthenticated && context.oneSignal.User.externalId) {
                    let service = await context.getService<IPushService>('IPushService');

                    console.debug('Touching push address ....................', {
                        appType: getPushAppType(context),
                        externalId: context.oneSignal.User.externalId
                    });

                    let res = await service.touchPushAddress({
                        appType: getPushAppType(context),
                        externalId: context.oneSignal.User.externalId
                    });

                    console.debug('Touching result ....................', res);

                    if (res.ok) {
                        if (!context.oneSignal.User.PushSubscription.optedIn) {
                            await context.oneSignal.User.PushSubscription.optIn();
                        }
                        context.settings[Constants.Settings.ExternalId] = context.oneSignal.User.externalId;
                    } else {
                        await initializer.register(context);
                    }
                }
            }
        }
        catch (err) {
            console.log(err);
        }
    }

    private async register(context: IAppUserContext) {
        try {
            var initializer = this;
            if (context.isAuthenticated && context.oneSignal) {
                let service = await context.getService<IPushService>('IPushService');

                if (context.oneSignal.User.externalId) {
                    var deregister = await initializer.deregister(context);
                    if (!deregister.ok) {
                        return;
                    }
                }

                console.debug('Registering push address ....................', {
                    appType: getPushAppType(context)
                });

                let res = await service.registerPushAddress({
                    appType: getPushAppType(context)
                });

                console.debug('Registering result ....................', res);

                if (res.ok && res.message) {
                    let externalId = res.message;
                    console.debug("Logging in: " + externalId);
                    await context.oneSignal.login(externalId);
                    context.settings[Constants.Settings.ExternalId] = context.oneSignal.User.externalId ?? '';
                    console.debug("Logged in: " + context.oneSignal.User.externalId);
                }
            }
        }
        catch (err) {
            console.log(err);
        }
    }

    private async deregister(context: IAppUserContext) {
        try {
            if (context.oneSignal) {
                if (context.oneSignal.User.externalId) {
                    let service = await context.getService<IPushService>('IPushService');

                    console.debug('Deregistering push address ....................', {
                        appType: getPushAppType(context),
                        externalId: context.oneSignal.User.externalId
                    });
                    var res = await service.deregisterPushAddress({
                        appType: getPushAppType(context),
                        externalId: context.oneSignal.User.externalId
                    });
                    console.debug('Deregistering result ....................', res);

                    if (!res.ok) {
                        console.debug('Error de-registering address');
                    }

                    await context.oneSignal.logout();
                    delete context.settings[Constants.Settings.ExternalId];
                    return { ok: true };
                }
            }
            return { ok: false };
        }
        catch (err) {
            console.log(err);
            return { ok: false };
        }
    }

    private async configurePush(context: IAppUserContext) {
        var initializer = this;
        try {
            if (context.oneSignal && context.oneSignal.Notifications.isPushSupported()) {

                context.oneSignal.Notifications.addEventListener("click", function (value: NotificationClickEvent) {
                    context.events.emit(Constants.Events.NotificationClicked, value.notification.notificationId, convertNotification(value.notification));
                });
                context.oneSignal.Notifications.addEventListener("foregroundWillDisplay", async function (value: NotificationForegroundWillDisplayEvent) {
                    //TODO: browser does not support preventing display value.preventDefault();
                    if (document.visibilityState === 'visible') {
                        try {
                            value.preventDefault();
                        } catch (err) {
                            console.log(err);
                        }
                    }
                    var handler = new NotificationReceivedEventHandler(value.notification.notificationId, convertNotification(value.notification));
                    var res = await context.triggerNotificationReceived(handler) as NotificationReceivedEventHandler;
                    if (!res.isCancelled) {
                        context.events.emit(Constants.Events.NotificationReceived, value.notification.notificationId, convertNotification(value.notification));
                    }
                });
                context.oneSignal.Notifications.addEventListener("dismiss", function (value: NotificationDismissEvent) {
                    let notificationId = '';
                    let message = undefined;
                    if ((value as unknown as IOSNotification).notificationId) {
                        notificationId = (value as unknown as IOSNotification).notificationId;
                        message = convertNotification((value as unknown as IOSNotification));
                    } else {
                        notificationId = value.notification?.notificationId;
                        message = convertNotification(value.notification);
                    }

                    context.events.emit(Constants.Events.NotificationDismissed, notificationId, message);
                });
                context.oneSignal.Notifications.addEventListener("permissionChange", async function (value: boolean) {
                    console.log("permissionChange", value);
                    await initializer.handlePermissionChange(context, true);
                    context.events.emit(Constants.Events.NotificationPermissionChanged);
                });

                initializer.handlePermissionChange(context, context.oneSignal.User.externalId ? true : false);
            }
        }
        catch (err) {
            console.log(err);
            console.log('Push not supported');
        }
    }

    async initialize(context: IUserContext): Promise<void> {
        console.debug('Running OneSignalInitializer');
        var initializer = this;
        let appContext = context as IAppUserContext;
        let id = getId(context);

        if (window.Notification) {
            if (id) {
                try {
                    let oneSignal = useOneSignal();
                    //NOTE: does not await so that if onesignal is blocked it does not kill the app
                    initializer.internalInitialize(appContext, id, oneSignal);
                }
                catch (err) {
                    console.log(err);
                }
            } else {
                console.log('Onesignal app id not configured');
            }
        } else {
            console.log('Browser does not support push notifications');
        }

    }

    private async internalInitialize(context: IAppUserContext, id: string, oneSignal: IOneSignalOneSignal) {
        var initializer = this;
        try {
            await oneSignal.init({
                appId: id
            });

            //only assigns onesignal if it successfully initializes
            context.oneSignal = oneSignal;
            await initializer.configurePush(context);
            context.events.emit(Constants.Events.NotificationsInitialized);

            if (context.settings['osnpr'] == 'true') {
                //remove setting
                delete context.settings.osnpr;
                await context.requestNotificationPermission();
            }

        } catch (err) {
            console.log(err);
            console.log('Browser does not support push notifications');
        }
    }
}